/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import LeftChevron from 'aquilla/icons/actions/LeftChevron';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { useEffect, useState } from 'react';
import Drawer from '@base/Drawer/Drawer';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useRouter } from 'next/router';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import CounsellorHelp from './BaseComponents/CounsellorHelp';
import UpgradePlanSelectionFlow from './UpgradePlanSelectionFlow/UpgradePlanSelectionFlow';
import NormalPlanSelection from './NormalPlanSelectionFlow/NormalPlanSelection';
import ExtendPlanSelection from './ExtendPlanSelectionFlow/ExtendPlanSelection';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  grid-column: span 24;
  margin-top: -${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_4B};
`;

export const TopWrapper = styled.div<{ $showBackButton?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: ${({ $showBackButton }) =>
    $showBackButton && `${Spacings.SPACING_12B}`};
  margin-bottom: ${({ $showBackButton }) =>
    !$showBackButton && `${Spacings.SPACING_4B}`};
  align-items: ${({ $showBackButton }) => $showBackButton && 'center'};
  justify-content: space-between;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
`;

export const StyLeftChevron = styled(LeftChevron)``;

const StyledHelpDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    padding: 0;
    border-top-left-radius: ${Spacings.SPACING_4B};
    border-top-right-radius: ${Spacings.SPACING_4B};
  }
`;

export const HelpWrapper = styled.div<{ $showBackButton?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_6};
  width: 77px;
  margin-top: ${({ $showBackButton }) =>
    !$showBackButton && `${Spacings.SPACING_2B}`};
  height: ${Spacings.SPACING_8B};
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-i-black);
  border-radius: ${Spacings.SPACING_12B};
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
`;

export const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
`;

const Loader = styled(CircularLoader)`
  margin: auto;
`;

export const isPurchaseAllowed = (price, isPlusUser) => {
  return price?.is_purchase_allowed === 1 || !isPlusUser;
};

export const isUpgradeRefundApplicable = (price, isPlusUser) => {
  return (
    isPlusUser && isPurchaseAllowed(price, isPlusUser) && price?.upgrade_refund
  );
};

export const FLOWSTEPS = {
  ISNORMALFLOW: 1,
  ISUPGRADEFLOW: 2,
  ISEXTENDFLOW: 3
};

const NewPlanSelection = () => {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState<boolean>(false);
  const {
    query: { goalUID = '', is_upgrade = false, is_extend = false } = {}
  } = useRouter();
  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));

  const [flowSteps, setFlowSteps] = useState(FLOWSTEPS?.ISNORMALFLOW);

  useEffect(() => {
    if (is_upgrade) {
      setFlowSteps(FLOWSTEPS?.ISUPGRADEFLOW);
    } else if (isPlusUser || is_extend) {
      setFlowSteps(FLOWSTEPS?.ISEXTENDFLOW);
    }
  }, [isPlusUser, is_upgrade, is_extend]);

  const RenderFlowComponent = () => {
    if (flowSteps) {
      switch (flowSteps) {
        case FLOWSTEPS?.ISNORMALFLOW:
          return (
            <NormalPlanSelection setIsHelpDrawerOpen={setIsHelpDrawerOpen} />
          );
        case FLOWSTEPS?.ISEXTENDFLOW:
          return (
            <ExtendPlanSelection setIsHelpDrawerOpen={setIsHelpDrawerOpen} />
          );
        case FLOWSTEPS?.ISUPGRADEFLOW:
          return (
            <UpgradePlanSelectionFlow
              setIsHelpDrawerOpen={setIsHelpDrawerOpen}
              setFlowSteps={setFlowSteps}
            />
          );
        default:
          return (
            <NormalPlanSelection setIsHelpDrawerOpen={setIsHelpDrawerOpen} />
          );
      }
    } else {
      return <Loader color="var(--color-i-black)" />;
    }
  };

  return (
    <>
      <Wrapper>{RenderFlowComponent()}</Wrapper>
      <StyledHelpDrawer
        drawerState={isHelpDrawerOpen}
        hideCloseButton
        anchor="bottom"
        onClose={() => {
          setIsHelpDrawerOpen(false);
        }}
      >
        <CounsellorHelp
          onClose={() => {
            setIsHelpDrawerOpen(false);
          }}
        />
      </StyledHelpDrawer>
    </>
  );
};

export default NewPlanSelection;
